import React, {forwardRef, Fragment, useContext, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react';
import {Button} from '@/components/app/button';
import {ContentHints} from '@/components/app/hint';
import {Hierarchy, ExpandedNodes} from '@/components/app/hierarchy';
import {type HierarchyNode} from '@/components/app/hierarchy';
import {cn} from '@/lib/utils';
import {Icon} from '../../../app/components/Icon';
import {SearchBar} from '../../../app/components/SearchBar';
import {TextMatch} from '../../../app/components/TextMatch';
import {RelevantCondition} from '../../biotwin/types';
import {PersonContext} from '../../person/contexts/PersonContext';
import {type TableOfContents} from '../helpers/TableOfContents';
import {BiobookChapter, BiobookFilter, BiobookPage as IBiobookPage} from '../types';
import styles from './BiobookChapter.module.css';
import {BiobookPage} from './BiobookPage';
import {defaultMechanismLabelColor, getCompareBiobookPages} from '../utils';
export interface BiobookTableOfContentsProps {
  biobookPages?: IBiobookPage[];
  noEmptyRoots?: boolean;
  onSelectBiobookChapter?: (biobookChapter: BiobookChapter) => void | boolean;
  selectedConditions: RelevantCondition[];
  selectedFilter?: BiobookFilter | null;
  tableOfContents: TableOfContents;
}
export interface BiobookTableOfContentsHandle {
  collapseNode: (node: number | string | HierarchyNode) => void;
  expandNode: (node: number | string | HierarchyNode) => void;
  hideDetails: (node?: number | string | HierarchyNode) => void;
  showBiobookPage: (biobookPage: number | IBiobookPage) => void;
  showDetails: (node: number | string | HierarchyNode) => void;
  toggleDetails: (node: number | string | HierarchyNode) => void;
}
export const BiobookTableOfContents = forwardRef<BiobookTableOfContentsHandle, BiobookTableOfContentsProps>(
  ({biobookPages, noEmptyRoots, onSelectBiobookChapter, selectedConditions, selectedFilter, tableOfContents}, ref) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedKeys, setExpandedKeys] = useState<ExpandedNodes>({});
    const treeContainer = useRef<HTMLDivElement>(null);
    const [searchResults, setSearchResults] = useState<HierarchyNode[]>(tableOfContents.flatNodes);
    const [detailedKey, setDetailedKey] = useState<string | number>();
    const [selectedGroup, setSelectedGroup] = useState<ChapterDetailsGroup | null>(null);
    const positionLockCache = useRef<{
      groupId: undefined | null | string;
      key: undefined | number | string;
      top: number;
    } | null>(null);
    const [prevTableOfContents, setPrevTableOfContents] = useState<TableOfContents>(tableOfContents);
    useImperativeHandle(
      ref,
      () => ({
        collapseNode: (node) => {
          const key = typeof node === 'object' ? `${node.key}` : node;
          setExpandedKeys({
            ...expandedKeys,
            [key]: false,
          });
        },
        expandNode: (node) => {
          const key = typeof node === 'object' ? `${node.key}` : node;
          setExpandedKeys({
            ...expandedKeys,
            [key]: true,
          });
        },
        hideDetails: (node) => {
          const key = typeof node === 'object' ? `${node.key}` : node;
          if (detailedKey != null && (key == null || detailedKey === key)) {
            setDetailedKey(undefined);
            setSelectedGroup(null);
          }
        },
        showBiobookPage: (input) => {
          const biobookPage = typeof input === 'number' ? biobookPages?.find((bp) => bp.phraseId === input) : input;
          if (biobookPage == null) {
            return;
          }
          const node = tableOfContents.flatNodes.find((node) =>
            biobookPage.bodyLifeMapLabelNames.includes(node.data.biobookChapter.labelName),
          );
          if (node != null) {
            lockNodeElementPosition(node.key);
            const ascendantNodes = tableOfContents.getAscendantNodes(node);
            setExpandedKeys(
              ascendantNodes.reduce(
                (acc, node) => ({
                  ...acc,
                  [`${node.key}`]: true,
                }),
                expandedKeys,
              ),
            );
            setDetailedKey(node.key);
            setSelectedGroup({
              id: 'all',
              managementLabelName: '',
              mechanismLabelName: '',
              value: 0,
            });
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const phraseElement = treeContainer.current?.querySelector<HTMLDivElement>(
                  `[data-phraseid="${biobookPage.phraseId}"]`,
                );
                if (phraseElement != null) {
                  phraseElement.scrollIntoView({
                    behavior: 'smooth',
                  });
                  phraseElement.classList.add('blink');
                }
              });
            });
          }
        },
        showDetails: (node) => {
          const key = typeof node === 'object' ? `${node.key}` : node;
          lockNodeElementPosition(key);
          setDetailedKey(key);
          setSelectedGroup(null);
        },
        toggleDetails: (node) => {
          const key = typeof node === 'object' ? `${node.key}` : node;
          if (detailedKey === key) {
            setDetailedKey(undefined);
          } else {
            lockNodeElementPosition(key);
            setDetailedKey(key);
          }
          setSelectedGroup(null);
        },
      }),
      [biobookPages, tableOfContents, expandedKeys, detailedKey, selectedGroup],
    );

    // auto scrolls to the first search result
    useLayoutEffect(() => {
      const mark = treeContainer.current?.querySelector('mark');
      const pTreeContainer = mark?.closest<HTMLElement>('[data-tree-root]');
      const pTreeNode = mark?.closest<HTMLElement>('[data-tree-node]');
      treeContainer.current?.scrollTo(0, (pTreeNode?.offsetTop ?? 0) - (pTreeContainer?.offsetTop ?? 0));
    }, [searchQuery]);

    // tries to prevent moving of detailed node or selected group
    useLayoutEffect(() => {
      if (positionLockCache.current != null) {
        const {groupId, key, top} = positionLockCache.current;
        const scrollContainer = treeContainer.current;
        const nodeElement = treeContainer.current
          ?.querySelector<HTMLDivElement>(`[data-node-key="${key}"]`)
          ?.closest<HTMLDivElement>('[data-tree-node]');
        const groupElement = groupId
          ? nodeElement?.querySelector<HTMLDivElement>(`[data-group-id="${groupId}"]`)
          : null;
        if (scrollContainer != null) {
          if (groupElement != null) {
            scrollContainer.scrollTo({
              top: groupElement.offsetTop - top,
              behavior: 'instant',
            });
          } else if (nodeElement != null) {
            scrollContainer.scrollTo({
              top: nodeElement.offsetTop - top,
              behavior: 'instant',
            });
          }
        }
        positionLockCache.current = null;
      }
    }, [detailedKey, expandedKeys, selectedGroup]);
    if (prevTableOfContents !== tableOfContents) {
      setPrevTableOfContents(tableOfContents);
      setExpandedKeys({});
      setSearchQuery('');
      setSearchResults(tableOfContents.flatNodes);
      return null;
    }
    return (
      <div className="h-full flex flex-col" data-name="user-biobook-table-of-contents">
        <div className="px-6 shrink-0 text-center">
          <div className={'pt-6 pb-2 border-zinc-400'}>
            <div className="flex items-center">
              <div className="flex-1 min-w-0" />
              <div className="flex-1 min-w-0">Table of Contents</div>
              <div className="flex-1 min-w-0">
                <div
                  className="flex items-center"
                  style={{
                    height: 0,
                  }}
                >
                  <SearchBar
                    className="ml-auto"
                    onChange={(e) => {
                      const newSearchResults = e.value
                        ? tableOfContents.flatNodes.filter((node) => hasSearchResult(node, e.value))
                        : tableOfContents.flatNodes;
                      setSearchQuery(e.value);
                      setSearchResults(newSearchResults);
                      setExpandedKeys(Object.fromEntries(searchResults.map((node) => [node.key!, true])));
                    }}
                    placeholder="Search"
                    value={searchQuery}
                  />
                </div>
              </div>
            </div>
            {(selectedConditions.length > 0 || selectedFilter != null) && (
              <div className="text-sm text-blue-400 mt-2">
                Pages specific for {selectedFilter === 'favorites' && <strong>'Bookmarks'</strong>}
                {selectedFilter === 'multiIndexed' && <strong>'Multi-indexed'</strong>}
                {selectedFilter === 'drugMultiIndexed' && <strong>'Drug Multi-indexed'</strong>}
                {selectedFilter === 'neoplastic' && <strong>'Neoplastic'</strong>}
                {selectedFilter === 'standardAndPolicy' && <strong>'Standard & Policy'</strong>}
                {selectedFilter === 'updates' && <strong>'Updates'</strong>}
                {selectedConditions.length > 0 && selectedFilter != null && <span className="underline"> AND </span>}
                {selectedConditions.map(({conditionName}, index) => (
                  <Fragment key={index}>
                    {index > 0 && <span className="underline"> AND </span>}
                    <strong>'{conditionName.trim()}'</strong>
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto" ref={treeContainer}>
          {searchQuery && searchResults.length === 0 && (
            <p className={'p-6 my-0 text-muted-foreground'}>No results found.</p>
          )}
          {(!searchQuery || searchResults.length > 0) && (
            <Hierarchy
              className={styles.tree}
              expanded={expandedKeys}
              renderNode={(node: HierarchyNode) => {
                const {phraseCount} = node.data.biobookChapter;
                const {phraseCount: filteredPhraseCount = 0} = node.data.filteredBiobookChapter ?? {};
                const nameElementId = `table-of-contents-biobook-chapter-${node.data.biobookChapter.labelId}-name`;
                const detailed = detailedKey === node.key;
                return (
                  <div className={'flex-1 self-start'} data-node-key={node.key}>
                    <div
                      className={'flex items-center'}
                      style={{
                        height: 45,
                      }}
                    >
                      <div className="flex flex-1 items-center">
                        <span className="text-indigo-400 pr-1">{node.data.path.join('.')}.</span>{' '}
                        <Button
                          type="button"
                          variant="link"
                          data-tree-action
                          className="h-auto p-0 text-left font-normal whitespace-normal text-foreground"
                          id={nameElementId}
                          onClick={() => {
                            const result = onSelectBiobookChapter?.(node.data.biobookChapter);
                            if (result !== false) {
                              lockNodeElementPosition(node.key);
                              setDetailedKey(detailed ? undefined : node.key);
                              setSelectedGroup(null);
                            }
                          }}
                        >
                          <TextMatch matcher={searchQuery} text={node.label ?? ''} />
                          <ContentHints side="top" target={`#${nameElementId}`}>
                            Click to read pages
                          </ContentHints>
                        </Button>
                        <div
                          className={cn(styles.phraseCount, {
                            [styles.phraseCountEmpty]: phraseCount === 0,
                          })}
                          onClick={() => {
                            const result = onSelectBiobookChapter?.(node.data.biobookChapter);
                            if (result !== false) {
                              lockNodeElementPosition(node.key);
                              setDetailedKey(detailed ? undefined : node.key);
                              setSelectedGroup(null);
                            }
                          }}
                        >
                          {phraseCount === 0 && node.data.descendentPhraseCount > 0 && (
                            <span className={styles.phraseCountDescendentValue}>{node.data.descendentPhraseCount}</span>
                          )}
                          <div className={styles.phraseCountValue}>
                            {phraseCount === 0 ? (
                              <Icon className="text-xs" icon="minus" />
                            ) : noEmptyRoots && filteredPhraseCount !== phraseCount ? (
                              `${filteredPhraseCount} / ${phraseCount}`
                            ) : (
                              phraseCount
                            )}
                          </div>
                        </div>
                        <div
                          className="self-end border-0 border-b border-zinc-400 border-dotted flex-1 min-w-0 mx-2 relative"
                          style={{
                            top: -2,
                          }}
                        />
                        <span className="text-muted-foreground">{node.data.pageNumber}</span>
                      </div>
                    </div>
                    {detailed && (
                      <div
                        className={'mt-2 mb-4'}
                        style={{
                          marginLeft: -53,
                        }}
                      >
                        <ChapterDetails
                          biobookPages={biobookPages?.filter((biobookPage) =>
                            biobookPage.bodyLifeMapLabelNames.includes(node.data.biobookChapter.labelName),
                          )}
                          onChangeGroupSelection={({group}) => {
                            lockNodeElementPosition(node.key, group?.id);
                            setSelectedGroup(group);
                          }}
                          selectedGroup={selectedGroup}
                        />
                      </div>
                    )}
                  </div>
                );
              }}
              renderToggle={(node: HierarchyNode, options) => {
                return (
                  <div
                    className={'flex shrink-0 items-center justify-center mr-4'}
                    style={{
                      height: 45,
                      width: 45,
                    }}
                  >
                    <Button
                      className={cn(
                        cn('shrink-0 text-primary', {
                          'rotate-90': options.expanded,
                          hidden: !node.children?.length,
                        }),
                        'rounded-full',
                      )}
                      onClick={(e) => {
                        lockNodeElementPosition(node.key);
                        setDetailedKey(undefined);
                        setSelectedGroup(null);
                        return options.onClick(e);
                      }}
                      type={'button'}
                      variant="ghost"
                      startIcon={<Icon icon={'chevron-right'} />}
                      aria-label={`${options.expanded ? 'Collapse' : 'Expand'} ${node.label ?? 'chapter'}`}
                      size="icon"
                    />
                  </div>
                );
              }}
              onExpandedChange={(e) => setExpandedKeys(e)}
              nodes={tableOfContents.nodes}
            />
          )}
        </div>
      </div>
    );
    function hasSearchResult(node: HierarchyNode, searchQuery: string): boolean {
      return Boolean(
        node.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.children?.some((childNode: HierarchyNode) => hasSearchResult(childNode, searchQuery)),
      );
    }
    function lockNodeElementPosition(key: undefined | number | string, groupId?: string | null) {
      const scrollContainer = treeContainer.current;
      const nodeElement = treeContainer.current
        ?.querySelector<HTMLDivElement>(`[data-node-key="${key}"]`)
        ?.closest<HTMLDivElement>('[data-tree-node]');
      const groupElement = groupId ? nodeElement?.querySelector<HTMLDivElement>(`[data-group-id="${groupId}"]`) : null;
      if (scrollContainer != null) {
        if (groupElement != null) {
          positionLockCache.current = {
            groupId,
            key,
            top: groupElement.offsetTop - scrollContainer.scrollTop,
          };
        } else if (nodeElement != null) {
          positionLockCache.current = {
            groupId,
            key,
            top: nodeElement.offsetTop - scrollContainer.scrollTop,
          };
        }
      } else {
        positionLockCache.current = null;
      }
    }
  },
);
interface ChapterDetailsProps {
  biobookPages?: IBiobookPage[];
  onChangeGroupSelection?: (event: {group: ChapterDetailsGroup | null}) => void;
  selectedGroup?: ChapterDetailsGroup | null;
}
interface ChapterDetailsGroup {
  id: string;
  managementLabelName: string;
  mechanismLabelName: string;
  value: number;
}
const ChapterDetails = ({biobookPages = [], onChangeGroupSelection, selectedGroup}: ChapterDetailsProps) => {
  const {hasRelation, relevantConditionsDeduplicated, userId} = useContext(PersonContext);
  const managementLabelNames = biobookPages
    .slice()
    .sort((a, b) => a.managementLabelShowOrder - b.managementLabelShowOrder)
    .map((biobookPage) => biobookPage.managementLabelName)
    .filter((managementLabelName, index, self) => self.indexOf(managementLabelName) === index);
  const mechanismLabelColors = biobookPages
    .slice()
    .sort((a, b) => a.mechanismLabelShowOrder - b.mechanismLabelShowOrder)
    .map((biobookPage) => biobookPage.mechanismLabelColor)
    .filter((mechanismLabelColor, index, self) => self.indexOf(mechanismLabelColor) === index);
  const mechanismLabelNames = biobookPages
    .slice()
    .sort((a, b) => a.mechanismLabelShowOrder - b.mechanismLabelShowOrder)
    .map((biobookPage) => biobookPage.mechanismLabelName)
    .filter((mechanismLabelName, index, self) => self.indexOf(mechanismLabelName) === index);
  const groups = mechanismLabelNames.flatMap((mechanismLabelName) => {
    return managementLabelNames.map((managementLabelName) => {
      return {
        id: `${managementLabelName}-${mechanismLabelName}`,
        managementLabelName,
        mechanismLabelName,
        value: getValue(managementLabelName, mechanismLabelName),
      };
    });
  }) as ChapterDetailsGroup[];
  const groupAll = {
    id: 'all',
    managementLabelName: '',
    mechanismLabelName: '',
    value: biobookPages.length,
  };
  const visibleBiobookPages =
    selectedGroup == null
      ? []
      : selectedGroup.id === 'all'
        ? biobookPages
        : biobookPages.filter(
            (biobookPage) =>
              biobookPage.managementLabelName === selectedGroup.managementLabelName &&
              biobookPage.mechanismLabelName === selectedGroup.mechanismLabelName,
          );
  const visibleGroups = [groupAll].concat(groups.filter(({value}) => value > 0));
  const compareBiobookPages = getCompareBiobookPages(relevantConditionsDeduplicated ?? [], hasRelation);
  const sortedVisibleBiobookPages = visibleBiobookPages.slice().sort(compareBiobookPages);
  return (
    <div className={'p-6 bg-gray-100 rounded-3xl'}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
        }}
      >
        {selectedGroup != null && (
          <div
            style={{
              gridRowStart: Math.floor(visibleGroups.findIndex((group) => group.id === selectedGroup.id) / 3) + 2,
              gridColumnEnd: 'span 3',
            }}
          >
            <div className={'flex flex-col gap-4'}>
              {sortedVisibleBiobookPages.map((biobookPage) => (
                <BiobookPage
                  biobookPage={biobookPage}
                  key={biobookPage.phraseId}
                  showBodyLifeMapLabels={false}
                  showMatchedConditions
                  showManagementLabel={selectedGroup.id === 'all'}
                  showMechanismLabel={selectedGroup.id === 'all'}
                  style={{
                    scrollMarginTop: '0.875rem',
                  }}
                  userId={userId}
                />
              ))}
            </div>
          </div>
        )}
        {visibleGroups.map((group, index) => {
          return <Fragment key={index}>{renderGroup(group)}</Fragment>;
        })}
      </div>
    </div>
  );
  function getValue(managementLabelName: string, mechanismLabelName: string) {
    return biobookPages.filter(
      (biobookPage) =>
        biobookPage.managementLabelName === managementLabelName &&
        biobookPage.mechanismLabelName === mechanismLabelName,
    ).length;
  }
  function renderGroup(group: ChapterDetailsGroup) {
    const {id, managementLabelName, mechanismLabelName, value} = group;
    const color = mechanismLabelColors[mechanismLabelNames.indexOf(mechanismLabelName)] ?? defaultMechanismLabelColor;
    const selected = selectedGroup?.id === id;
    return (
      <Button
        type="button"
        variant="ghost"
        aria-pressed={selected}
        aria-label={group.id === 'all' ? 'All pages' : `${mechanismLabelName} ${managementLabelName}`}
        className={`h-auto flex flex-col items-center justify-center flex-1 min-w-0 p-4 rounded-xl text-nowrap cursor-pointer transition-all duration-200`}
        data-group-id={group.id}
        onClick={() =>
          onChangeGroupSelection?.({
            group: selected ? null : group,
          })
        }
        style={{
          backgroundColor: color,
          boxShadow: selected ? `0 0 0 1px white, 0 0 0 3px ${color}` : undefined,
        }}
      >
        <div className={'flex flex-1 flex-col items-center gap-1 text-sm mb-2'}>
          {mechanismLabelName && (
            <div
              className={cn(`flex   items-center   gap-2`, {
                'opacity-0': mechanismLabelName.toLowerCase() === 'no mechanism',
              })}
            >
              <Icon icon={'pentagon-half'} />
              {mechanismLabelName}
            </div>
          )}
          {managementLabelName && (
            <div className={'flex items-center gap-2'}>
              {managementLabelName.startsWith('Act') && <Icon icon={'capsule-pill'} />}
              {managementLabelName.startsWith('Think') && <Icon icon={'question-square'} />}
              {managementLabelName}
            </div>
          )}
          {group.id === 'all' && (
            <div className={'text-center my-auto'}>
              <div className={'flex items-center gap-2'}>
                <Icon icon={'eye'} />
                All
              </div>
            </div>
          )}
        </div>
        <div
          className={'rounded-full bg-white mx-auto flex items-center justify-center'}
          style={{
            height: '2.25rem',
            width: '2.25rem',
          }}
        >
          {value}
        </div>
      </Button>
    );
  }
};
